import { customSeriesDefaultOptions } from 'lightweight-charts';

export const CHARTING_OHLC_COLORS = { upColor: '#0ea5e9', downColor: '#ef4444' };

export function isChartingOhlcBar(bar) {
    return Boolean(bar) && ['open', 'high', 'low', 'close'].every(key => Number.isFinite(bar[key])) && bar.high >= bar.low;
}

export function toChartingOhlcData(candles) {
    return candles.filter(bar => bar && bar.time != null).map(bar => isChartingOhlcBar(bar)
        ? { ...bar, color: bar.close >= bar.open ? CHARTING_OHLC_COLORS.upColor : CHARTING_OHLC_COLORS.downColor }
        : { time: bar.time });
}

// Bitmap rectangles, not strokes: width must never lengthen the high/low stem.
// Ratios are separate because browser zoom can give unequal X/Y pixel ratios.
export function chartingOhlcGeometry(bar, barSpacing, horizontalPixelRatio, verticalPixelRatio, openVisible = true) {
    if (![bar.x, bar.highY, bar.lowY, bar.openY, bar.closeY, barSpacing, horizontalPixelRatio, verticalPixelRatio].every(Number.isFinite)
        || barSpacing <= 0 || horizontalPixelRatio <= 0 || verticalPixelRatio <= 0) return [];

    const desiredWidth = Math.min(5, Math.max(1, Math.floor(barSpacing / 6)));
    const width = Math.max(1, Math.round(Math.min(desiredWidth, barSpacing * 0.6) * horizontalPixelRatio));
    const center = bar.x * horizontalPixelRatio;
    const left = Math.round(center - width / 2);
    let top = Math.min(bar.highY, bar.lowY) * verticalPixelRatio;
    let bottom = Math.max(bar.highY, bar.lowY) * verticalPixelRatio;

    if (bottom - top >= 2) {
        // Snap inwards, never outside the real range. For subpixel ranges keep
        // the exact fractional bounds rather than inventing a minimum height.
        top = Math.ceil(top);
        bottom = Math.floor(bottom);
    } else if (top === bottom) {
        // A genuinely flat bar needs a one-device-pixel mark, centred on its
        // price. This bounded half-pixel exception does not grow with zoom.
        top -= 0.5;
        bottom += 0.5;
    }

    const height = bottom - top;
    const rectangles = [{ x: left, y: top, width, height }];
    if (barSpacing < 3) return rectangles;

    // Longer opening/closing marks when zoomed, with room between neighbours.
    const reach = Math.floor(Math.min(24, barSpacing * 0.45) * horizontalPixelRatio);
    // Match the stem's CSS thickness; clamp at the extremes and on tiny ranges.
    const tickHeight = Math.min(height, Math.max(1, Math.round(desiredWidth * verticalPixelRatio)));
    const tickTop = coordinate => Math.max(top, Math.min(bottom - tickHeight, Math.round(coordinate * verticalPixelRatio - tickHeight / 2)));
    const openLeft = Math.round(center - reach);
    const closeRight = Math.round(center + reach);
    if (openVisible && openLeft < left) rectangles.push({ x: openLeft, y: tickTop(bar.openY), width: left - openLeft, height: tickHeight });
    if (closeRight > left + width) rectangles.push({ x: left + width, y: tickTop(bar.closeY), width: closeRight - left - width, height: tickHeight });
    return rectangles;
}

export class ChartingOhlcSeries {
    constructor() {
        this.data = null;
        this.options = this.defaultOptions();
    }

    defaultOptions() {
        return { ...customSeriesDefaultOptions, ...CHARTING_OHLC_COLORS, color: CHARTING_OHLC_COLORS.upColor, openVisible: true };
    }

    priceValueBuilder(bar) {
        // Native autoscale includes both extremes; native last-price line and
        // crosshair use the last element. Display geometry never changes OHLC.
        return [bar.high, bar.low, bar.close];
    }

    isWhitespace(bar) {
        return !isChartingOhlcBar(bar);
    }

    renderer() {
        return this;
    }

    update(data, options) {
        this.data = data;
        this.options = options;
    }

    draw(target, priceToCoordinate) {
        if (!this.data?.visibleRange) return;
        const { bars, visibleRange, barSpacing } = this.data;
        const from = Math.max(0, visibleRange.from);
        const to = Math.min(bars.length, visibleRange.to);
        target.useBitmapCoordinateSpace(({ context, horizontalPixelRatio, verticalPixelRatio }) => {
            context.save();
            try {
                for (let index = from; index < to; index += 1) {
                    const item = bars[index];
                    const bar = item.originalData;
                    if (!isChartingOhlcBar(bar)) continue;
                    const rectangles = chartingOhlcGeometry({
                        x: item.x,
                        highY: priceToCoordinate(bar.high),
                        lowY: priceToCoordinate(bar.low),
                        openY: priceToCoordinate(bar.open),
                        closeY: priceToCoordinate(bar.close)
                    }, barSpacing, horizontalPixelRatio, verticalPixelRatio, this.options.openVisible);
                    context.fillStyle = item.barColor || bar.color || (bar.close >= bar.open ? this.options.upColor : this.options.downColor);
                    for (const rectangle of rectangles) context.fillRect(rectangle.x, rectangle.y, rectangle.width, rectangle.height);
                }
            } finally {
                context.restore();
            }
        });
    }

    destroy() {
        this.data = null;
    }
}
